/* eslint-disable */
import { usePremiumData } from "@/common/context/premiumInfoProvider";
import { useProductInfo } from "@/common/context/productInfoProvider";
import { CoverageItem, FormikTermData, Insured } from "@/common/data/formikData";
import { IPlan,
  IPlanAgeFaceAmountRange,
  IProductVariant,
  MinMaxValues,
  PlanKind,
  PlanType } from "@/common/data/productInfoInterfaces";
import { Sex } from "@/common/data/quoteInterfaces";
import useWatchAndRender from "@/common/hooks/useWatchAndUpdate";
import { Indexer } from "@/common/types/indexer";
import { HasErrors, ValidatableEditor } from "@/common/types/interfaces";
import { secondInsuredIndex } from "@/common/utils/constants";
import { calculateAge, checkValidationForAddPayor, isDwpRider, isNumberValue, scrollToError, validateDwpRidersAvailabilityBasedOnTotalPremiums } from "@/common/utils/helpers";
import { getNearestAge } from "@/components/ci/helpers/ciHelpers";
import { EqOptionsFields } from "@equitable/equx";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import React, { createContext, RefObject, useContext, useRef, useState } from "react";

export type RiderEditorStatus = "closed" | "adding" | number;
export interface PlanInfo {
  plan: IPlan; indexOfCoverage: number | undefined;
}

export interface IRenderModel {
  currentProductVariant: IProductVariant | undefined;
  disableAddInsured: boolean;
  disableRemoveInsured: boolean;
  basePlansInfoToRender: PlanInfo[][]; // [insuredIndex][planIndex]
  showRidersSection: Indexer<boolean>; // [insuredId]
  showRiderSelector: Indexer<boolean>; // [insuredId]
  ridersInfoToSelect: EqOptionsFields[][]; // [insuredIndex][riderIndex]
  ratingImpactedRiders: Indexer<string[]>;
  hidePolicyRiderSection: boolean;
  showDwpRidersImpactedDialog: boolean;
  hasInsuredLevelDwpRider: boolean;
  hasPolicyLevelDwpRider: boolean;
}

export interface ModelValidation extends Indexer<InsuredValidation> {
  hasErrors: boolean;
  [key: number]: InsuredValidation; // key - index of insured
  editRider: PlanValidation; // plan validation info for currently editing rider
  maxTotalCoverageAmountPerInsured: number;
  productMinMaxLives: MinMaxValues;
  productVariantMinMaxLives: MinMaxValues;
  productVariantMinMaxAge: MinMaxValues;
  isModelValidForIllustration: boolean;
}

export interface InsuredValidation {
  isDirty: boolean;
  hasErrors: boolean;
  plans: Indexer<PlanValidation>; // [planId]
  noSelectedBasePlan: boolean; // no plans selected
  noSelectedBasePlanWithFaceAmount: boolean; // no plan selected with valid/invalid face amount entered 
  noValidSelectedBasePlan: boolean; // no plan selected with valid face amount entered 
  noValidAge: boolean;
  noValidMandatoryFields: boolean;
  ageProvided: boolean;
  totalCoverageExceeded: boolean;
  hasRiders: boolean;
  inCompleteRatingInfo: boolean;
  invalidRiderDueToRating: boolean;
}

export interface PlanValidation {
  hasErrors: boolean;
  isSelected: boolean; // was plan selected?
  isFirstSelected: boolean; // was plan selected before others?
  minAge: number; // min/max age for current plan
  maxAge: number;
  invalidAge: boolean | null; // true - invalid, false - valid, null - unknown as age is not provided
  minFaceAmount: number; // min/max face amount  for current plan for provided age
  maxFaceAmount: number;
  invalidFaceAmount: boolean | null; // true - invalid, false - valid, null - unknown as face amount or age not provided
  criticalIllnessRiderPremiumInfo?: CriticalIllnessRiderPremiumInfo;
}

export interface CriticalIllnessRiderPremiumInfo {
  minExpiryAge: number;
  maxExpiryAge: number;
  invalidExpiryAge: boolean | null;
  cirPremiumTypeSelected: boolean;
}

export interface IRenderModelContext {
  updateFormikValues: (formik: FormikProps<FormikTermData>) => void;
  model: IRenderModel;
  validation: ModelValidation;
  togglePlan: (
    insuredIndex: number,
    planId: number,
    value?: CoverageItem
  ) => CoverageItem[];
  editorActionType: RiderEditorStatus;
  toggleRiderEditor: (value: RiderEditorStatus) => boolean;
  validationMode: ValidationMode;
  setValidationMode: (mode: ValidationMode) => void;
  policyMode: PolicyMode;
  setPolicyMode: (mode: PolicyMode) => void;
  setDirty: (value?: boolean) => void;
  forceRender: () => void;
  isAddingPolicy: boolean;
  setIsAddingPolicy: (val: boolean) => void;
  resetModel: () => void;
  rebuildModel: (formik: FormikProps<FormikTermData>) => void;
  viewReportPolicyRiderMode: boolean;
  setViewReportPolicyRiderMode: (value: boolean) => void;
  checkPayorErrorMessage: () => boolean;
  hasSignificantChanges: (newValues: FormikTermData, oldValues: FormikTermData) => boolean;
  setRider: (formik: FormikProps<FormikTermData>, insuredIndex: number, value?: CoverageItem) => void;
  addRider: (formik: FormikProps<FormikTermData>) => void;
  isAddRiderError: boolean;
  setIsAddRiderError: (value: boolean) => void;
  editRiderRef: React.RefObject<ValidatableEditor>;
  alertRiderRef: RefObject<HasErrors>;
  cancelRider: (formik: FormikProps<FormikTermData>) => void;
  updateRider: (formik: FormikProps<FormikTermData>) => void;
  cancelPolicyRider: (formik: FormikProps<FormikTermData>, dataType: string) => void;
  addPolicyRider: (formik: FormikProps<FormikTermData>, dataType: string) => void;
}

const defaultModel: IRenderModel = {
  currentProductVariant: undefined,
  disableAddInsured: false,
  disableRemoveInsured: false,
  basePlansInfoToRender: [],
  showRidersSection: {},
  showRiderSelector: {},
  ridersInfoToSelect: [],
  ratingImpactedRiders: {},
  hidePolicyRiderSection: false,
  showDwpRidersImpactedDialog: false,
  hasInsuredLevelDwpRider: false,
  hasPolicyLevelDwpRider: false
};

const defaultPlanValidation: PlanValidation = {
  hasErrors: false,
  isSelected: false,
  isFirstSelected: false,
  minAge: 0,
  maxAge: 0,
  invalidAge: false,
  minFaceAmount: 0,
  maxFaceAmount: 0,
  invalidFaceAmount: false
};

const defaultValidation: ModelValidation = {
  hasErrors: false,
  maxTotalCoverageAmountPerInsured: 0,
  productMinMaxLives: { min: 0, max: 0 },
  productVariantMinMaxLives: { min: 0, max: 0 },
  productVariantMinMaxAge: { min: 0, max: 0 },
  editRider: { ...defaultPlanValidation },
  isModelValidForIllustration: false
};

export enum ValidationMode {
  saveDraft = 1,
  illustration = 2,
  viewReport = 3,
  apply = 4
}

export enum PolicyMode {
  default,
  add,
  edit
}

const RenderModelProvider = ({ children }: any) => {

  const productInfo = useProductInfo();
  const [model, setModel] = useState<IRenderModel>({ ...defaultModel });

  const validation = useRef<ModelValidation>({
    ...defaultValidation
  });

  const formikValues = useRef<FormikTermData | undefined>(undefined);
  const [policyMode, setPolicyMode] = useState<PolicyMode>(PolicyMode.default);
  const [isAddingPolicy, setIsAddingPolicy] = useState(false);
  const [isAddRiderError, setIsAddRiderError] = useState<boolean>(false);
  const editRiderRef = useRef<ValidatableEditor>(null);
  const alertRiderRef = useRef<HasErrors>(null);
  const refValidationMode = useRef<ValidationMode>(ValidationMode.saveDraft);
  const setValidationMode = (value: ValidationMode) => refValidationMode.current = value;
  const { premiumData } = usePremiumData();

  const [viewReportPolicyRiderMode, setViewReportPolicyRiderMode] = useState(false);
  const refDirty = useRef<number[]>([]);
  const setDirty = (value: boolean = true) => {

    refDirty.current = !!value && formikValues.current != null
      ? formikValues.current?.insureds.map(a => a.id)
      : [];

    formikValues.current?.insureds.forEach((insured: Insured, insuredIndex: number) => {
      if (validation.current[insuredIndex] != null) {
        validation.current[insuredIndex].isDirty = refDirty.current.includes(insured.id);
      }
    });

    validation.current = { ...validation.current };
  };

  const correctDirty = () => {
    refDirty.current = refDirty.current.filter(a => formikValues.current?.insureds.some(b => b.id === a));
  };

  useWatchAndRender([model, validation.current, refValidationMode.current]);

  // rider editor helper
  // requirements:
  // - user can open add rider editor only if all other closed
  // - user can't open edit rider editor if an adding editor open
  // - user can open editing rider editor if another editing editor open, then second closes first with loosing changes
  // while opening an editor provide EditorActionType info, for editing proide random number
  // wait for openingEditorId change and check id, if the same, then ignore, else close self
  const [editorActionType, setEditorActionType] = useState<RiderEditorStatus>("closed");
  const toggleRiderEditor = (value: RiderEditorStatus): boolean => {
    if (
      editorActionType === "closed" ||
      value === "closed" ||
      (isNumberValue(editorActionType) && isNumberValue(value))
    ) {
      setEditorActionType(value);

      return true;
    }

    return false;
  };

  const forceRender = () => {
    setModel({ ...model });
    validation.current = { ...validation.current };
  };

  const resetModel = () => {
    setModel({ ...defaultModel });
    validation.current = {
      editRider: {} as PlanValidation,
      hasErrors: false,
      maxTotalCoverageAmountPerInsured: 0,
      productMinMaxLives: { min: 0, max: 0 },
      productVariantMinMaxAge: { min: 0, max: 0 },
      productVariantMinMaxLives: { min: 0, max: 0 },
      isModelValidForIllustration: false
    };
  };

  const rebuildModel = (formik: FormikProps<FormikTermData>) => {

    if (
      formikValues == null ||
      productInfo.data == null ||
      productInfo.mappedData == null
    ) {
      return;
    }

    const newModel: IRenderModel = { ...defaultModel };
    const insureds = formikValues.current?.insureds ?? [];
    const isJoint = formikValues.current?.isJoint ?? false;
    correctDirty();

    if (insureds.length === 0) {
      resetModel(); // no insureds data

      return;
    }

    newModel.currentProductVariant = productInfo.getProductVariant(
      insureds.length,
      isJoint
    );
    formik.setFieldValue(
      "productVariantId",
      newModel.currentProductVariant?.id
    );

    if (newModel.currentProductVariant == null) {
      resetModel(); // unable to detect product variant

      return;
    }

    // base plans, available for current configuration
    const availableBasePlans = productInfo.getAvailablePlans(
      false,
      insureds.length,
      isJoint
    );

    if (premiumData?.annualPremium > productInfo.data.maxTotalYearlyPremiumAmountForDisabilityWaiverOfPremium) {
      // hiding policy level dwp rider on reaching premiums above 50k annually
      newModel.hidePolicyRiderSection = true;
      // show Dialog to remove policy rider when it's added or in edit state
      if (formik.values.policyRider != null || formik.values.editPolicyRider != null) {
        newModel.showDwpRidersImpactedDialog = true;
        newModel.hasPolicyLevelDwpRider = true;
      }

      // show Dialog to remove dwp rider when it's added at insured level
      for (const insured of insureds) {
        if (insured.riderItems.some(r => isDwpRider(productInfo.constants.codesWaiverOfPremium,
          productInfo.mappedData.plans[r.planId].code))) {

          newModel.showDwpRidersImpactedDialog = true;
          newModel.hasInsuredLevelDwpRider = true;
          break;
        }
      }

      // show Dialog to remove dwp rider when it's in edit state and premium is more than 50k annual or zero
      if (formik.values.editRider != null &&
        isDwpRider(productInfo.constants.codesWaiverOfPremium, productInfo.mappedData.plans[formik.values.editRider.planId].code)) {
        newModel.showDwpRidersImpactedDialog = true;
        newModel.hasInsuredLevelDwpRider = true;
      }
    }

    // for every insured return a list of plans that are available for currect policy configuration
    // or were previously selected
    // and index of the corresponding coverage (if the plan was selected)
    newModel.basePlansInfoToRender = insureds.map((insured: Insured) =>
      productInfo!
        .data!.plans.filter((a) => a.planType === PlanType.BasePlan)
        .map((a) => {
          const theIndex = insured.basePlanItems.findIndex(
            (b) => b.planId === a.id
          );
          const isAvailable = availableBasePlans.some((b) => b.id === a.id);
          const result =
            isAvailable || theIndex != -1 // plan either available for selection or already selected
              ? ({
                  plan: a,
                  indexOfCoverage: theIndex == -1 ? undefined : theIndex
                } as PlanInfo)
              : null;

          return result;
        })
        .filter((a) => a != null)
        .map((a) => a!)
    );

    // riders
    const availableRiders = productInfo.getAvailablePlans(
      true,
      insureds.length,
      isJoint
    );
    // for every insured return a list of available riders for currect policy configuration for the dropdown
    newModel.ridersInfoToSelect = insureds.map(
      (insured: Insured, insuredIndex: number) =>
        allMandatoryFieldsToSelectRiderArePopulated(
          insured,
          newModel.currentProductVariant!
        )
          ? availableRiders
              .filter(
                (a) =>
                  a.planType === PlanType.Rider &&
                  a.planKind === PlanKind.Insured
              ) // only riders of insured (not policy) level
              .filter((a) => !insured.riderItems.some((b) => b.planId === a.id)) // that are not selected yet
              .filter((a) =>
                insured.ratingInfo == null ||
                !productInfo.constants.codesRatingImpactedRiders.includes(a.code)
              )
              .filter((a) =>
                productInfo.isPlanAvailableForInsured(
                  newModel.currentProductVariant!,
                  a.id,
                  insuredIndex,
                  insured.age
                )
              )
              .filter((a) => validateDwpRidersAvailabilityBasedOnTotalPremiums(
                premiumData?.annualPremium,
                productInfo.data.maxTotalYearlyPremiumAmountForDisabilityWaiverOfPremium,
                a.code,
                productInfo.constants.codesWaiverOfPremium
              ))
              .sort((a: IPlan, b: IPlan) =>
                a.name > b.name ? 1 : a.name < b.name ? -1 : 0
              ) // sort by name
              .map((a) => ({ id: a.id, label: a.name, value: a.id })) // select only data needed for dropdown
          : []
    );

    newModel.ratingImpactedRiders = insureds.map(
      (insured: Insured, insuredIndex: number) => {

        if (insured.ratingInfo == null) {
          return [];
        }

        const availableRiders = newModel.ridersInfoToSelect[insuredIndex];
        const selectedRiders = [ ...insured.riderItems ];

        if (
          formik.values.editRider != null &&
          !selectedRiders.some(a => a.planId === formik.values.editRider?.planId)
        ) {
          selectedRiders.push(formik.values.editRider);
        }

        if (selectedRiders.length === 0) {
          return [];
        }

        const availableRiderIds = new Set(availableRiders.map(r => r.id));

        const ridersImpacted = selectedRiders.filter(r => !availableRiderIds.has(r.id) &&
          productInfo.constants.codesRatingImpactedRiders.includes(productInfo.mappedData.plans[r.planId].code));

        const result =
          ridersImpacted.map(rider =>
            productInfo.mappedData.plans[rider.planId].name
          );

        return result;
      });

    insureds.forEach((insured: Insured, index: number) => {
      // save indexed result if rider section (list of riders + selector) must be shown
      newModel.showRidersSection[insured.id] =
        insured.riderItems.length > 0 ||
        formikValues.current?.editRider != null ||
        allMandatoryFieldsToSelectRiderArePopulated(insured, newModel.currentProductVariant!);

      // save indexed result if all mandatory information for current insurd entered to show rider selector
      newModel.showRiderSelector[insured.id] =
          (formikValues.current?.editRider != null ||
            allMandatoryFieldsToSelectRiderArePopulated(
              insured,
              newModel.currentProductVariant!
            )) &&
            insured.riderItems.length < productInfo.constants.maxNumberOfRidersForInsured;

      const dateOfBirth = dayjs(insured.dateOfBirth);

      if (insured.useBackDate) {
        formik.setFieldValue(`insureds.${index}.age`, calculateAge(dateOfBirth));
      } else {
        if (dateOfBirth?.isValid()) {
          formik.setFieldValue(`insureds.${index}.age`, getNearestAge(dateOfBirth));
        }
      }
    });

    if (formik.values.editPolicyRider != null || formik.values.policyRider != null) {
      const isEditRiderField = formik.values.editPolicyRider != null;
      const path = isEditRiderField ? "editPolicyRider" : "policyRider";
      const info = isEditRiderField
        ? formik.values.editPolicyRider?.payorDisabilityWaiverOfPremiumRiderInfo
        : formik.values.policyRider?.payorDisabilityWaiverOfPremiumRiderInfo;
      const dateOfBirthWaiver = dayjs(info?.dateOfBirth);

      if (info?.useBackDate) {
        formik.setFieldValue(`${path}.payorDisabilityWaiverOfPremiumRiderInfo.age`, calculateAge(dateOfBirthWaiver));
      } else {
        if (dateOfBirthWaiver?.isValid()) {
          formik.setFieldValue(`${path}.payorDisabilityWaiverOfPremiumRiderInfo.age`, getNearestAge(dateOfBirthWaiver));
        }
      }
    }

    // #region validation model building
    const newValidation: ModelValidation = {
      hasErrors: false,
      maxTotalCoverageAmountPerInsured: productInfo.data.maxTotalCoverageAmountPerInsured,
      productMinMaxLives: {
        min: Math.min(
          ...productInfo.data.productVariants.map((a) => a.minNumberOfLives)
        ),
        max: Math.max(
          ...productInfo.data.productVariants.map((a) => a.maxNumberOfLives)
        )
      },
      productVariantMinMaxLives: {
        min: newModel.currentProductVariant.minNumberOfLives,
        max: newModel.currentProductVariant.maxNumberOfLives
      },
      productVariantMinMaxAge: {
        min: newModel.currentProductVariant.minAge,
        max: newModel.currentProductVariant.maxAge
      },

      editRider:
        formik.values.editRiderInsuredIndex != null &&
        isNumberValue(formik.values.editRider?.planId) &&
        formik.values.editRider?.planId > 0
          ? getPlanValidation(
            // for the coverage that is currently being edited
              insureds[formik.values.editRiderInsuredIndex],
              productInfo.mappedData.plans[formik.values.editRider.planId!],
              newModel.currentProductVariant.id,
              true
            )
          : { ...defaultPlanValidation }, // default values in case no coverage is being edited
      isModelValidForIllustration: false
    };

    newValidation.hasErrors = newValidation.editRider.hasErrors;

    insureds.forEach((insured: Insured, insuredIndex: number) => {

      const insuredToProcess = { ...insured };
      const insuredValidation: InsuredValidation = {
        isDirty: refDirty.current.includes(insuredToProcess.id),
        hasErrors: false,
        plans: {} as Indexer<PlanValidation>,
        noSelectedBasePlan: false,
        noSelectedBasePlanWithFaceAmount: false,
        noValidSelectedBasePlan: false,
        noValidAge: !isNumberValue(insuredToProcess.age) ||
          insuredToProcess.age < newModel.currentProductVariant!.minAge ||
          insuredToProcess.age > newModel.currentProductVariant!.maxAge,
        noValidMandatoryFields: false,
        ageProvided: isNumberValue(insuredToProcess.age),
        totalCoverageExceeded: (insuredToProcess.basePlanItems
          .reduce((acc, coverage) => (acc ?? 0) + (coverage.faceAmount ?? 0), 0) ?? 0) > newValidation.maxTotalCoverageAmountPerInsured,
        hasRiders: insuredToProcess.riderItems.some(a => productInfo.mappedData.plans[a.planId].planType === PlanType.Rider),
        inCompleteRatingInfo: false,
        invalidRiderDueToRating: newModel.ratingImpactedRiders[insuredIndex]?.length > 0 // if ADB rider is added and rating is checked we can later add any other rider which is invalid by adding rating.
      };

      if (newModel.currentProductVariant?.isJoint && insuredIndex === secondInsuredIndex) {
        // for validation purposes copy base plans of first insured to second one,
        // so second one 'virtually' has them
        insuredToProcess.basePlanItems = [...insureds[0].basePlanItems];
      }

      productInfo.data.plans.forEach((plan: IPlan) => {
        const planValidation: PlanValidation = getPlanValidation(insuredToProcess, plan, newModel.currentProductVariant!.id);
        insuredValidation.plans[plan.id] = planValidation;
      });

      insuredValidation.noSelectedBasePlan =
        insuredToProcess.basePlanItems.length === 0;

      insuredValidation.noSelectedBasePlanWithFaceAmount =
        insuredValidation.noSelectedBasePlan ||
        insuredToProcess.basePlanItems.every(a => insuredValidation.plans[a.planId].invalidFaceAmount === null);

      insuredValidation.noValidSelectedBasePlan =
        insuredValidation.noSelectedBasePlan ||
        insuredToProcess.basePlanItems.every(a => insuredValidation.plans[a.planId].invalidFaceAmount !== false);

      insuredValidation.noValidMandatoryFields =
        insuredValidation.noValidAge ||
        !(insuredToProcess.sex === Sex.Male || insuredToProcess.sex === Sex.Female) || typeof insuredToProcess.isSmoker !== "boolean";

      insuredValidation.inCompleteRatingInfo =
        insured.ratingInfo != null &&
        (insured.ratingInfo?.percentageId == null ||
          insured.ratingInfo?.durationYears == null ||
          (insured.ratingInfo?.permanent == null &&
            insured.ratingInfo?.temporal == null &&
            insured.ratingInfo.percentageId === productInfo.constants.defaultRatingPercentageId
          )
        );

      insuredValidation.hasErrors =
        insuredValidation.noSelectedBasePlan ||
        insuredValidation.noValidSelectedBasePlan ||
        insuredValidation.noValidMandatoryFields ||
        insuredValidation.totalCoverageExceeded ||
        insuredValidation.invalidRiderDueToRating ||
        Object.keys(insuredValidation.plans)
          .map(a => insuredValidation.plans[a as unknown as number])
          .some(a => a.isSelected && a.hasErrors);

      newValidation.isModelValidForIllustration = !insuredValidation.hasErrors;

      insuredValidation.hasErrors ||= insuredValidation.inCompleteRatingInfo;
      newValidation.hasErrors ||= insuredValidation.hasErrors;

      newValidation[insuredIndex] = insuredValidation;
    });

    // #endregion

    newModel.disableRemoveInsured =
      insureds.length <= newValidation.productMinMaxLives.min;
    newModel.disableAddInsured =
      insureds.length >= newValidation.productMinMaxLives.max || newModel.currentProductVariant?.isJoint;

    setModel(newModel);
    validation.current = newValidation;

    if (validation.current.hasErrors) {
      scrollToError();
    }
  };

  const getPlanValidation = (insured: Insured, plan: IPlan, productVariantId: number, isEditRider: boolean = false): PlanValidation => {
    const coverage: CoverageItem | undefined =
      isEditRider
        ? formikValues.current?.editRider
        : (
            plan.planType === PlanType.BasePlan
              ? insured.basePlanItems
              : insured.riderItems
          ).find((a: CoverageItem) => a.planId === plan.id)!;

    const isPlanSelected: boolean = coverage != null;
    const isFirstSelected: boolean = plan.planType === PlanType.BasePlan && insured.basePlanItems[0] === coverage;
    const isFaceAmountProvided: boolean | undefined = isNumberValue(coverage?.faceAmount);
    const isAgeProvided: boolean = isNumberValue(insured?.age);

    // age, face amount ranges for the plan id and current product variant
    const planAgeFaceAmountRanges: IPlanAgeFaceAmountRange[] =
      productInfo.mappedData.planAgeFaceAmountRanges[plan.id][
        productVariantId
      ];

    const planValidation: PlanValidation = {
      ...defaultPlanValidation,
      isSelected: isPlanSelected,
      isFirstSelected: isFirstSelected
    };

    // if there are ranges for the plan for current product variant, validate age aand face amount, else do not validate
    if (planAgeFaceAmountRanges != null) {
      // planAgeFaceAmountRange for current insured
      const range: IPlanAgeFaceAmountRange | undefined =
        planAgeFaceAmountRanges.find(
          (a) =>
            isAgeProvided &&
            ((a.minAge === 0 && a.maxAge === 0) ||
              (insured.age! >= a.minAge && insured.age! <= a.maxAge))
        );

      if (range == null) {
        // range not found: insured.age is out of allowed ages for the plan
        planValidation.minAge = Math.min(
          ...planAgeFaceAmountRanges.map((a) => a.minAge)
        ); // find min age from all ranges
        planValidation.maxAge = Math.max(
          ...planAgeFaceAmountRanges.map((a) => a.maxAge)
        ); // find max age from all ranges
        planValidation.invalidAge = // age was provided, but not is in any range - invalid, min & max are zero - valid, if not provided - unknown
          isAgeProvided
            ? true
            : planValidation.minAge === 0 && planValidation.maxAge === 0
              ? false
              : null;
        planValidation.minFaceAmount = Math.min(
          ...planAgeFaceAmountRanges.map((a) => a.minFaceAmount)
        ); // find min face amount from all ranges
        planValidation.maxFaceAmount = Math.max(
          ...planAgeFaceAmountRanges.map((a) => a.maxFaceAmount)
        ); // find max face amount from all ranges
      } else {
        planValidation.minAge = range.minAge;
        planValidation.maxAge = range.maxAge;
        planValidation.invalidAge = false; // since the age provided and the range for it is found
        planValidation.minFaceAmount = range.minFaceAmount;
        planValidation.maxFaceAmount = range.maxFaceAmount;
      }

      // doublecheck with product variant age range
      const currProductVariant = productInfo.mappedData.productVariants[productVariantId];
      if (currProductVariant.minAge > planValidation.minAge || planValidation.minAge === 0) {
        planValidation.minAge = currProductVariant.minAge;
      }

      if (currProductVariant.maxAge < planValidation.maxAge || planValidation.maxAge === 0) {
        planValidation.maxAge = currProductVariant.maxAge;
      }

      // process here the plans that require custom logic
      if (plan.code === productInfo.constants.codeAdditionalAccidentalDeathBenefitRider) {

        const faceAmounts = insured.basePlanItems
          .map(a => a.faceAmount)
          .filter(a => isNumberValue(a))
          .map(a => a!);

        const faceAmountOfABasePlanToValidateAgainst: number | undefined = faceAmounts[0]; // take first added base plan face amount

        if (isNumberValue(faceAmountOfABasePlanToValidateAgainst) && faceAmountOfABasePlanToValidateAgainst < planValidation.maxFaceAmount) {
          planValidation.maxFaceAmount = faceAmountOfABasePlanToValidateAgainst;
        }

      } else if (plan.code === productInfo.constants.codeCriticalIllnessRider && isAgeProvided && coverage?.criticalIllnessRiderInfo != null) {

        planValidation.minAge = Math.min(...productInfo.data.criticalIllnessRiderPremiumTypes.map((a) => a.minAge)); // find min age from all ranges
        planValidation.maxAge = Math.max(...productInfo.data.criticalIllnessRiderPremiumTypes.map((a) => a.maxAge)); // find max age from all ranges

        const ciRiderPremiumType = coverage.criticalIllnessRiderInfo.premiumTypeId != null
          ? productInfo.mappedData.criticalIllnessRiderPremiumTypes[coverage.criticalIllnessRiderInfo.premiumTypeId]
          : undefined;

        planValidation.criticalIllnessRiderPremiumInfo = {
          minExpiryAge: insured.age! + productInfo.constants.cirMinDifferenceBetweenInsuredAgeAndUptoAgeValue,
          maxExpiryAge: ciRiderPremiumType != null ? ciRiderPremiumType.maxExpiryAge : 0, // initial value for type 'number'
          invalidExpiryAge: false,
          cirPremiumTypeSelected: ciRiderPremiumType != null
        };

        if (ciRiderPremiumType != null) {
          planValidation.maxAge = ciRiderPremiumType.maxAge;
        }

        planValidation.invalidAge = isNumberValue(insured.age)
          ? insured.age < planValidation.minAge || insured.age > planValidation.maxAge
          : null;

        planValidation.criticalIllnessRiderPremiumInfo.invalidExpiryAge = isNumberValue(coverage.criticalIllnessRiderInfo.expiryAge)
          ? coverage.criticalIllnessRiderInfo.expiryAge < planValidation.criticalIllnessRiderPremiumInfo.minExpiryAge ||
            coverage.criticalIllnessRiderInfo.expiryAge > planValidation.criticalIllnessRiderPremiumInfo.maxExpiryAge
          : null;
      }

      planValidation.invalidFaceAmount = isFaceAmountProvided
        ? coverage!.faceAmount! < planValidation.minFaceAmount ||
          coverage!.faceAmount! > planValidation.maxFaceAmount
        : null; // face amount not provided - unknown

      planValidation.hasErrors =
        planValidation.invalidAge ||
        planValidation.invalidFaceAmount === true ||
        planValidation.criticalIllnessRiderPremiumInfo?.invalidExpiryAge === true ||
        planValidation.criticalIllnessRiderPremiumInfo?.cirPremiumTypeSelected === false;
    }

    return planValidation;
  };

  const togglePlan = (
    insuredIndex: number,
    planId: number,
    value?: CoverageItem
  ): CoverageItem[] => {
    const insured = formikValues.current?.insureds[insuredIndex];

    if (insured?.basePlanItems == null) {
      return [];
    }

    const plan = productInfo!.data!.plans.find((a: IPlan) => a.id === planId)!;

    const coverages =
      plan.planType === PlanType.BasePlan
        ? [...insured.basePlanItems]
        : [...insured.riderItems];

    let coverage = coverages.find((a) => a.planId === planId);

    if (coverage == null) {
      coverage =
        value != null
          ? value
          : {
              id: 0,
              planId: plan.id,
              faceAmount: null,
              uiHint: plan.uiHint,
              ratingInfo: null
            };

      if (model.currentProductVariant?.isJoint && plan.planType === PlanType.BasePlan && coverages.length > 0) {
        coverages.splice(0, coverages.length);
      }

      coverages.push(coverage);

    } else {
      coverages.splice(coverages.indexOf(coverage), 1);
    }

    return coverages;
  };

  const allMandatoryFieldsToSelectRiderArePopulated = (
    insured: Insured,
    currentProductVariant: IProductVariant
  ): boolean => {
    const result =
      (insured.age != null && insured.age >= currentProductVariant.minAge && insured.age <= currentProductVariant.maxAge) &&
      (insured.sex ?? 0) != 0 &&
      insured.isSmoker != null &&
      ((insured.basePlanItems.length > 0 &&
        insured.basePlanItems.every((a) => isNumberValue(a.faceAmount))) || // for joint plan it is enough to have a base plan selected for a first insured
        (currentProductVariant!.isJoint &&
          formikValues.current!.insureds[0].basePlanItems.length > 0 &&
          formikValues.current!.insureds[0].basePlanItems.every((a) => isNumberValue(a.faceAmount))
        ));

    return result;
  };

  const updateFormikValues = (formik: FormikProps<FormikTermData>) => {
    if (formik?.values == null) {
      return;
    }

    const result = hasSignificantChanges(
      formik.values,
      formikValues.current
    );
    formikValues.current = { ...formik.values };
    if (result) {
      rebuildModel(formik);
      formik.validateForm();
    }
  };

  const hasSignificantChanges = (
    newValues?: FormikTermData,
    oldValues?: FormikTermData
  ): boolean => {
    // if certain formik values have changed, then the model needs rebuilding
    if (typeof newValues !== "object") {
      return false;
    }

    if (typeof oldValues !== "object" && typeof newValues === "object") {
      return true;
    }

    oldValues = oldValues!;

    if (
      oldValues.productVariantId !== newValues.productVariantId ||
      oldValues.isJoint !== newValues.isJoint ||
      oldValues.insureds.length !== newValues.insureds.length ||
      oldValues.premiumPaymentMode != newValues.premiumPaymentMode
    ) {
      return true;
    }

    const changed = oldValues.insureds
      .map((oldInsured: Insured, index: number) => {
        const newInsured = newValues.insureds[index];
        const result =
          oldInsured.dateOfBirth !== newInsured.dateOfBirth ||
          oldInsured.age !== newInsured.age ||
          oldInsured.useBackDate !== newInsured.useBackDate ||
          oldInsured.sex !== newInsured.sex ||
          oldInsured.isSmoker !== newInsured.isSmoker ||
          oldInsured.ratingInfo != newInsured.ratingInfo ||
          oldInsured.basePlanItems.length !== newInsured.basePlanItems.length ||
          oldInsured.basePlanItems[0]?.planId !== newInsured.basePlanItems[0]?.planId ||
          oldInsured.riderItems.length !== newInsured.riderItems.length ||
          oldInsured.basePlanItems.map((oldInsuredBasePlan: CoverageItem, index: number) =>
            oldInsuredBasePlan.faceAmount !== newInsured.basePlanItems[index].faceAmount
          )
            .includes(true) ||
            oldInsured.basePlanItems[0]?.faceAmount !== newInsured.basePlanItems[0]?.faceAmount; // trigger this case to validate ADB rider against "first added base plan" face amount

        return result;
      })
      .includes(true);

    if (changed) {
      return true;
    }

    if (oldValues.editRider !== newValues.editRider) {
      return true;
    }

    if (oldValues.editPolicyRider != newValues.editPolicyRider) {
      return true;
    }

    if (oldValues.policyRider != newValues.policyRider) {
      return true;
    }

    return false;
  };

  const checkPayorErrorMessage = (): boolean => {
    const policyRiderInfo = formikValues.current?.editPolicyRider ? formikValues.current?.editPolicyRider : formikValues.current?.policyRider;

    return policyRiderInfo == null
      ? false
      : (
          refValidationMode.current == ValidationMode.apply &&
          policyRiderInfo &&
          policyRiderInfo?.payorDisabilityWaiverOfPremiumRiderInfo !==
          null &&
            (!policyRiderInfo?.payorDisabilityWaiverOfPremiumRiderInfo
              ?.firstName ||
              !policyRiderInfo?.payorDisabilityWaiverOfPremiumRiderInfo
                ?.lastName ||
                !policyRiderInfo?.payorDisabilityWaiverOfPremiumRiderInfo
                  ?.dateOfBirth)
        );
  };
  
  const setRider = (formik: FormikProps<FormikTermData>, insuredIndex: number, value?: CoverageItem|null) => {
    formik.setFieldValue("editRider", value);
    formik.setFieldValue(
      "editRiderInsuredIndex",
      value == null ? null : insuredIndex
    );
  }

  const addRider =(formik: FormikProps<FormikTermData>) => {
    if (formik.values?.editRider == null) {
      setIsAddRiderError(true);
      return;
    }
    
    const { editRider, editRiderInsuredIndex } = formik.values;
    if (editRiderInsuredIndex != null && editRider) {
      const list = togglePlan(
          editRiderInsuredIndex,
          formik.values.editRider?.planId!,
          { ...formik.values.editRider } as CoverageItem
        );
      formik.setFieldValue(`insureds[${editRiderInsuredIndex}].riderItems`, list);
      
      setRider(formik, editRiderInsuredIndex, undefined);

      toggleRiderEditor("closed");
    }
  }

  const cancelRider = (formik: FormikProps<FormikTermData>) => {
    setIsAddRiderError(false);
    toggleRiderEditor("closed");
    const { editRiderInsuredIndex } = formik.values;
    if (editRiderInsuredIndex != null) {
      setRider(formik, editRiderInsuredIndex, undefined);
    }
  }

  const cancelPolicyRider = (formik: FormikProps<FormikTermData>, dataType: string) => {
    const riderType =
      dataType === "payorDisabilityWaiverOfPremiumRiderInfo"
        ? "policyRider"
        : "editPolicyRider";
    toggleRiderEditor("closed");
    formik.setFieldValue("editPolicyRider", undefined);
    setPolicyMode(PolicyMode.default);
    const { [riderType]: removed, ...rest } = formik.touched;
    formik.setTouched(rest);
  }

  const addPolicyRider = (formik: FormikProps<FormikTermData>, dataType: string) => {
    setIsAddingPolicy(true);
    
    const riderType =
      dataType === "payorDisabilityWaiverOfPremiumRiderInfo"
        ? "editPolicyRider"
        : "policyRider";
    const isError = checkValidationForAddPayor(
      formik, riderType, { min: productInfo.data.minPayorAge, max: productInfo.data.maxPayorAge }
    );
    
    if (!isError) {
      const list = { ...formik.values.editPolicyRider };
      formik.setFieldValue("editPolicyRider", null);
      formik.setFieldValue("policyRider", list);
      toggleRiderEditor("closed");
      setPolicyMode(PolicyMode.default);
      setIsAddingPolicy(false);
    }
  }

  const updateRider = (formik: FormikProps<FormikTermData>) => {
    const { editRiderInsuredIndex } = formik.values;
    if (editRiderInsuredIndex != null) {  
      const { riderItems} = formik.values.insureds[editRiderInsuredIndex!];

      if (riderItems != null && riderItems.length > 0) {
        const riderIndex = riderItems.findIndex(a => a.planId === formik.values.editRider?.planId);

        formik.setFieldValue(
            `insureds[${editRiderInsuredIndex}].riderItems[${riderIndex}]`,
            { ...formik.values.editRider },
            true
          );
        setRider(formik, editRiderInsuredIndex!, undefined);

        toggleRiderEditor("closed");
      }
    }
  }

  return (
    <RenderModelContext.Provider
      value={{
        updateFormikValues,
        model,
        get validation(): ModelValidation {
          return validation.current;
        },
        togglePlan,
        editorActionType,
        toggleRiderEditor,
        get validationMode(): ValidationMode {
          return refValidationMode.current;
        },
        setValidationMode,
        get isAddRiderError(): boolean {
          return isAddRiderError;
        },
        setIsAddRiderError,
        editRiderRef,
        alertRiderRef,
        setDirty,
        forceRender,
        policyMode,
        setPolicyMode,
        isAddingPolicy,
        setIsAddingPolicy,
        resetModel,
        rebuildModel,
        viewReportPolicyRiderMode,
        setViewReportPolicyRiderMode,
        checkPayorErrorMessage,
        hasSignificantChanges,
        setRider,
        addRider,
        cancelRider,
        updateRider,
        cancelPolicyRider,
        addPolicyRider
      }}
    >
      {children}
    </RenderModelContext.Provider>
  );
};

export const RenderModelContext = createContext<IRenderModelContext>(
  undefined!
);
export const useRenderModel = () => useContext(RenderModelContext);
export default RenderModelProvider;
